import {
  DateCell,
  DateFormats,
  type DropdownMenuItemType,
  formatISODate,
  formatNumber,
  Grid,
  Icon,
  Inline,
  isRedacted,
  Link,
  RedactedWrapper,
  Tag,
  type TagIntent,
  Tooltip,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  employeesPath,
  useCompany,
  useCutoffDates,
  useEmployee,
  useEmployment,
  useLegalEntity,
} from '@omnipresentgroup/domains';
import { DetailsCard, DetailsSkeletonCard } from '@omnipresentgroup/shared';
import { OnboardingStatusValues } from '@omnipresentgroup/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  getCountryCode,
  getEmojiFlag,
  type TCountryCode,
} from 'countries-list';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { EmploymentStatusTag } from '../../employments';
import { ChangeUserEmailModal } from '../../users';
import { EmployeeEmploymentInfoModal } from '../employee-employment-info';

const DEFAULT_VALUE = '-';

const OnboardingStatusIntentMap: { [key: string]: TagIntent } = {
  [OnboardingStatusValues.ACTIVE]: 'success',
  [OnboardingStatusValues.INACTIVE]: 'default',
  [OnboardingStatusValues.INPROGRESS]: 'warning',
};

const OnboardingStatusTag = ({
  status,
}: {
  status: OnboardingStatusValues;
}) => {
  const intent = status ? OnboardingStatusIntentMap[status] : 'default';
  return (
    <Tag id={'onboarding-status'} intent={intent}>
      {status}
    </Tag>
  );
};

export const EmployeeOverview = ({
  employeeId,
  onEmploymentEdit,
  canEditInfo,
}: {
  employeeId: string;
  onEmploymentEdit?: (employmentId: string) => void;
  canEditInfo?: boolean;
}) => {
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { data: employee, isLoading: isLoadingEmployee } = useEmployee({
    id: employeeId,
    enabled: !!employeeId,
  });

  const { data: company, isLoading: isLoadingCompany } = useCompany({
    id: employee?.managerId,
    enabled: !!employee?.managerId,
  });

  const { data: employment, isLoading: isLoadingEmployment } = useEmployment({
    id: employee?.currentEmploymentId || '',
    enabled: !!employee?.currentEmploymentId,
  });

  const { data: cutoffDatesData } = useCutoffDates({
    legalEntityId: employee?.legalEntityId,
    enabled: Boolean(employee?.legalEntityId),
  });

  const { data: legalEntity } = useLegalEntity({
    id: employment?.legalEntityId || '',
  });

  const nextCutOffDate = useMemo(
    () => cutoffDatesData?.[0]?.cutoffDates?.[0],
    [cutoffDatesData],
  );

  const isLoading = useMemo(
    () => isLoadingEmployee || isLoadingCompany || isLoadingEmployment,
    [isLoadingEmployee, isLoadingCompany, isLoadingEmployment],
  );

  const fullName = `${employee?.personalDetails.firstName} ${employee?.personalDetails.lastName}`;

  const PersonalDetailsCard = () => {
    const [changeEmailModalOpen, setChangeEmailModalOpen] = useState(false);

    return (
      <DetailsCard
        id="personal-details"
        icon="User"
        title={fullName}
        tags={
          employee?.onboardingStatus?.status && (
            <OnboardingStatusTag status={employee.onboardingStatus.status} />
          )
        }
        actions={[
          {
            name: 'Copy id',
            onClick: () => {
              navigator.clipboard.writeText(employee?.id ?? '');
            },
            icon: 'Copy',
          },
          ...(isRedacted(employee?.contactDetails?.personalEmail)
            ? []
            : ([
                {
                  name: 'Copy email',
                  onClick: () => {
                    navigator.clipboard.writeText(
                      (employee?.contactDetails?.personalEmail as string) ?? '',
                    );
                  },
                  icon: 'Copy',
                },
                {
                  name: 'Change email',
                  onClick: () => setChangeEmailModalOpen(true),
                  icon: 'Edit',
                },
              ] satisfies DropdownMenuItemType[])),
        ]}
      >
        <Inline gap="4" align="center">
          {employee?.country.name && (
            <Icon
              icon={getEmojiFlag(
                getCountryCode(employee.country.name) as TCountryCode,
              )}
              size="16"
            />
          )}
          <Typography as="span" size="14" color="subtle">
            {employee?.country.name}
          </Typography>
          {!isRedacted(employee?.personalDetails?.privateAddress) && (
            <Tooltip
              id="location"
              description={[
                employee?.personalDetails?.privateAddress.addressLine1,
                employee?.personalDetails?.privateAddress.addressLine2,
                employee?.personalDetails?.privateAddress.addressLine3,
                employee?.personalDetails?.privateAddress.postalCode,
                employee?.personalDetails?.privateAddress.city,
                employee?.personalDetails?.privateAddress.state,
                employee?.personalDetails?.privateAddress.country,
              ]
                .filter(Boolean)
                .join(' • ')}
            >
              <Icon icon="MapPin" size="16" />
            </Tooltip>
          )}
        </Inline>
        <Inline gap="4" align="center">
          <Icon icon="Mail" size="16" />
          <RedactedWrapper value={employee?.contactDetails?.personalEmail}>
            <Typography as="span" size="14" color="subtle" ellipsis>
              {(employee?.contactDetails?.personalEmail as string) ||
                DEFAULT_VALUE}
            </Typography>
          </RedactedWrapper>
        </Inline>
        <Inline gap="4" align="center">
          <Icon icon="Phone" size="16" />
          <RedactedWrapper value={employee?.contactDetails?.phoneNumber}>
            <Typography as="span" size="14" color="subtle">
              {(employee?.contactDetails?.phoneNumber as string) ||
                DEFAULT_VALUE}
            </Typography>
          </RedactedWrapper>
        </Inline>
        {employee && changeEmailModalOpen && (
          <ChangeUserEmailModal
            key={`change-user-email-${employee.userId}`}
            modalProps={{
              open: changeEmailModalOpen,
              onOpenChange: setChangeEmailModalOpen,
            }}
            handleCancel={() => setChangeEmailModalOpen(false)}
            handleSuccess={() => {
              setChangeEmailModalOpen(false);
              queryClient.invalidateQueries({
                queryKey: [employeesPath, employee.id],
                refetchType: 'all',
              });
            }}
            userId={employee.userId}
          />
        )}
      </DetailsCard>
    );
  };

  const EmploymentCard = () => {
    const startDate = employment?.startDate
      ? formatISODate(employment.startDate || '', DateFormats.Date)
      : '';

    const terminationDate = employment?.terminationDate
      ? formatISODate(employment.terminationDate || '', DateFormats.Date)
      : '';

    const [employmentInfoModalOpen, setEmploymentInfoModalOpen] =
      useState(false);

    return (
      <DetailsCard
        id="employment-details"
        icon="Briefcase"
        title="Employment"
        tags={
          <>
            <Tag id="employment-type">{employment?.type}</Tag>
            {employment && <EmploymentStatusTag employment={employment} />}
          </>
        }
        actions={[
          {
            name: 'Edit',
            icon: 'Edit',
            onClick: () =>
              onEmploymentEdit?.(employee?.currentEmploymentId || ''),
          },
          {
            name: 'FAQ',
            icon: canEditInfo ? 'Edit' : 'Eye',
            onClick: () => setEmploymentInfoModalOpen(true),
          },
        ]}
      >
        <Link size="16" onClick={() => navigate(`/companies/${company?.id}`)}>
          {company?.name || ''}
        </Link>
        <Inline gap="4" align="center">
          <Typography as="span" size="14" color="subtle">
            {`${employee?.employmentDetails.jobTitle}`}
          </Typography>
          {employee?.employmentDetails.jobDescription && (
            <Tooltip
              description={employee?.employmentDetails.jobDescription}
              id="default-Tooltip"
            >
              <Icon icon="HelpCircle" size="16" />
            </Tooltip>
          )}
        </Inline>
        <Typography as="span" size="14" color="subtle">
          {`Employer: ${legalEntity?.name || DEFAULT_VALUE}`}
        </Typography>
        <Typography as="span" size="14" color="subtle">
          {terminationDate
            ? `Period: ${startDate} - ${terminationDate}`
            : `Start: ${startDate}`}
        </Typography>
        {employmentInfoModalOpen && employee && (
          <EmployeeEmploymentInfoModal
            key={`employee-information-${employee.id}`}
            employeeId={employee.id}
            modalProps={{
              open: employmentInfoModalOpen,
              onOpenChange: setEmploymentInfoModalOpen,
            }}
            handleCancel={() => setEmploymentInfoModalOpen(false)}
            handleSuccess={() => {
              setEmploymentInfoModalOpen(false);
            }}
            canEdit={canEditInfo}
          />
        )}
      </DetailsCard>
    );
  };

  const SalaryCard = () => {
    return (
      <DetailsCard
        id="salary-details"
        icon="Wallet"
        title="Salary"
        actions={[
          {
            name: 'Copy payroll id',
            onClick: () => {
              navigator.clipboard.writeText(
                employee?.payrollDetails?.payrollId ?? '',
              );
            },
            icon: 'Copy',
          },
        ]}
      >
        <RedactedWrapper
          value={employee?.employmentDetails.annualBaseSalary.amount || ''}
        >
          <Typography as="span" size="20" weight="medium">
            {formatNumber({
              input:
                Number(employee?.employmentDetails.annualBaseSalary.amount) ||
                0,
              currency: employee?.employmentDetails.annualBaseSalary.currency,
            })}
          </Typography>
        </RedactedWrapper>
        <Inline gap="4" align="center">
          <Typography as="span" size="14" color="subtle" whiteSpace="nowrap">
            Next cutoff:
          </Typography>
          <DateCell
            value={nextCutOffDate}
            dateFormat={DateFormats.DateTime}
            showTimezone
            color="subtle"
          />
        </Inline>
      </DetailsCard>
    );
  };

  return (
    <Grid>
      {isLoading ? (
        Array.from({ length: 3 }, (_, index) => (
          <DetailsSkeletonCard key={index} />
        ))
      ) : (
        <>
          <PersonalDetailsCard />
          <EmploymentCard />
          <SalaryCard />
        </>
      )}
    </Grid>
  );
};
